import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { ApiGetUserProfile, UserNameUpdated, UserPasswordUpdated } from '../../../stores/api/user/actions';
import { FromStore } from '../../../helpers/Store';
import Button from '../../../PublicSide/components/Button/Button';
import Navigation from '../../components/Navigation/Navigation';
import Input from '../../../PublicSide/components/Input/Input';
import { styled } from '@mui/system';
import { CenteredContainer, StyledBox } from '../../../style/GlobalsComponentsStyled';
import SuccessErrorMessage from '../../components/SuccesErrorMessage/SuccessErrorMessage';
import ErrorCode from '../../../api/dtos/ErrorCode';
import ServiceLocator from '../../../services/ServiceLocator';
import { HandleError, HandleSuccess, ShowErrorMessage } from '../../../stores/page/actions';
import { WithRefreshToken } from '../../../stores/auth/actions';
import { useAppDispatch } from '../../../helpers/UseAppDispatch';

const StyledBoxContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    minWidth: '100%',
    alignItems: 'strech',
    [theme.breakpoints.up('md')]: {
        minWidth: '30%',
    }
}));

const UserProfilePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = FromStore(state => state.apis.profile);
    const [name, setName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        dispatch(ApiGetUserProfile());
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            setName(user.name || '');
        }
    }, [user]);

    const handleSaveName = async () => {
        if (!name.trim()) {
            setErrorMessage('Name cannot be empty');
            return;
        }
        if (name === user?.name) {
            setErrorMessage('Name cannot be the same');
            return;
        }
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.UserProfileService.UpdateUserName(name);
            if (response.is_success) {
                dispatch(UserNameUpdated(name));
                dispatch(HandleSuccess('Name updated successfully!'));
                setIsEditingName(false);
            }
            else {
                dispatch(ShowErrorMessage('Error while updating name'));
            }
        }, dispatch);
    };

    const handleSavePassword = async () => {
        if (!oldPassword || !newPassword) {
            setErrorMessage('Both old and new passwords are required');
            return;
        }
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.UserProfileService.UpdateUserPassword({ oldPassword, newPassword });
            if (response.is_success) {
                dispatch(UserPasswordUpdated(newPassword));
                dispatch(HandleSuccess('Password updated successfully!'));
                setIsEditingPassword(false);
                setOldPassword('');
                setNewPassword('');
            }
            else if (response.error_code === ErrorCode.INVALID_PASSWORD) {
                dispatch(ShowErrorMessage(response.error_message ?? 'Invalid old password'));
            }
            else if (response.error_code === ErrorCode.SAME_OLD_NEW_PASSWORD) {
                dispatch(ShowErrorMessage(response.error_message ?? 'The new password cannot be the same as the old password'));
            }
            else if (response.error_code === ErrorCode.INVALID_FORMAT_PASSWORD) {
                setErrorMessage(response.error_message ?? "The password must be at least 6 characters long, one letter, one number, and one special character.");
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    return (
        <>
            <Navigation />
            <CenteredContainer className='main_content'>
                <Typography variant='h2'>User profile</Typography>
                <StyledBoxContainer>
                    <SuccessErrorMessage errorMessage={errorMessage} onClear={() => { setErrorMessage(''); }} />
                    <Box>
                        {!isEditingName && !isEditingPassword && (
                            <StyledBoxContainer>
                                <StyledBox>
                                    <Typography>Email : {user?.email}</Typography>
                                    {user?.name && <Typography>Name : {user.name}</Typography>}
                                </StyledBox>
                                <Box display='flex' gap='1rem' marginTop='1rem'>
                                    <Button text={user?.name ? 'Update name' : 'Add name'} onClick={() => setIsEditingName(true)} />
                                    <Button text='Change Password' onClick={() => setIsEditingPassword(true)} />
                                </Box>
                            </StyledBoxContainer>
                        )}
                        {isEditingName && (
                            <StyledBoxContainer>
                                <Input type='text' label='Name' value={name} onChange={(e) => setName(e.target.value)} />
                                <Box display='flex' gap='1rem'>
                                    <Button text='Cancel' onClick={() => setIsEditingName(false)} />
                                    <Button text='Save' onClick={handleSaveName} />
                                </Box>
                            </StyledBoxContainer>
                        )}
                        {isEditingPassword && (
                            <StyledBoxContainer>
                                <Input type='password' label='Old password' value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                                <Input type='password' label='New password' value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                <Box display='flex' gap='1rem'>
                                    <Button text='Cancel' onClick={() => setIsEditingPassword(false)} />
                                    <Button text='Save' onClick={handleSavePassword} />
                                </Box>
                            </StyledBoxContainer>
                        )}
                    </Box>
                </StyledBoxContainer>
            </CenteredContainer>
        </>
    );
};

export default UserProfilePage;

